import { roles } from '../../constants/roles';
import { DelegatedUsersTableProps } from './types';
import { InfoIcon } from '@g17eco/molecules/info-icon';
import { Button } from 'reactstrap';
import { QUESTION, SURVEY } from '@constants/terminology';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { StaffIcon } from '@g17eco/molecules/staff-icon';
import { hasAtLeastOneRoleLevel, isAssignedToAll, RoleLevel } from '@utils/delegation-users';
import { User } from '@api/survey-users';
import { DelegationUserText } from '@g17eco/molecules/delegation-user-text';
import { WorkgroupPermissions } from '@services/permissions/WorkgroupPermissions';
import { WorkgroupIcon } from '@g17eco/molecules/workgroup/WorkgroupIcon';
import { UtrvPermissions } from '@services/permissions/UtrvPermissions';
import { UtrvUserRole } from '@constants/user';

type UserRowProps = Pick<DelegatedUsersTableProps, 'utrvs' | 'handleRemoveUser' | 'isReadonly' | 'survey'> & {
  userRole: UtrvUserRole;
  user: User;
};

export const UserRow = (props: UserRowProps) => {
  const { userRole, user, utrvs, isReadonly } = props;
  return (
    <div className='d-flex align-items-center'>
      <CircleIcon userRole={userRole} user={user} utrvs={utrvs} />
      <DelegationUserText user={user} />
      {user.workgroups?.map((workgroup) =>
        WorkgroupPermissions.checkHasUtrvsUserRoles({
          utrvs,
          survey: { workgroups: user.workgroups ?? [] },
          userId: user._id,
          roles: [userRole],
        }) ? (
          <SimpleTooltip text={workgroup.name} key={workgroup._id}>
            <WorkgroupIcon workgroup={workgroup} className='ms-2' />
          </SimpleTooltip>
        ) : null,
      )}
      {user.isStaff ? <StaffIcon /> : null}
      {isReadonly ? null : <Action {...props} />}
    </div>
  );
};

const CircleIcon = ({ userRole, user, utrvs }: Pick<UserRowProps, 'utrvs' | 'userRole' | 'user'>) => {
  const isFullCircle = isAssignedToAll(userRole, user, utrvs?.length || 0);

  const icon = isFullCircle ? 'fa-circle' : 'fa-circle-half-stroke';
  const tooltip = `Delegated as a ${roles[userRole].shortName?.toLowerCase()} for ${
    isFullCircle ? 'all' : 'some'
  } of the ${QUESTION.PLURAL} selected.`;

  return (
    <SimpleTooltip text={tooltip}>
      <i className={`text-ThemeIconDark mr-3 fa-solid ${icon}`}></i>
    </SimpleTooltip>
  );
};

const Action = ({ utrvs, userRole, user, handleRemoveUser, survey }: UserRowProps) => {
  if (UtrvPermissions.canExplicitlyBulk({ utrvs, survey, user, role: userRole, every: false })) {
    return (
      <Button
        color='link'
        className='mx-1 p-0 ml-auto mr-2'
        onClick={() => handleRemoveUser({ roles: [userRole], userIds: [user._id] })}
      >
        <i className='fas fa-times text-ThemeDangerMedium' />
      </Button>
    );
  }

  if (hasAtLeastOneRoleLevel([RoleLevel.Initiative], userRole, user)) {
    const text = `User has the ${roles[
      userRole
    ].shortName?.toLowerCase()} permission assigned to them at the platform level and cannot be removed from ${
      QUESTION.SINGULAR
    } level delegation.`;
    return (
      <div className='ml-auto mr-2'>
        <InfoIcon text={text} />
      </div>
    );
  }

  if (hasAtLeastOneRoleLevel([RoleLevel.Survey], userRole, user)) {
    const text = `User has the ${roles[userRole].shortName?.toLowerCase()} permission assigned to them at the ${
      SURVEY.SINGULAR
    } level and cannot be removed from ${QUESTION.SINGULAR} level delegation.`;
    return (
      <div className='ml-auto mr-2'>
        <InfoIcon text={text} />
      </div>
    );
  }

  return null;
};
